import type { LeaderboardFilters } from "../types/filters.ts";

interface Props {
  filters: LeaderboardFilters;
  onPatch: (patch: Partial<LeaderboardFilters>) => void;
  onResetAll: () => void;
}

interface Chip {
  key: string;
  label: string;
  clear: Partial<LeaderboardFilters>;
}

function fmtUsd(n: number): string {
  if (n >= 1_000_000_000) return `$${(n / 1_000_000_000).toFixed(1)}B`;
  if (n >= 1_000_000) return `$${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `$${(n / 1_000).toFixed(1)}K`;
  return `$${n}`;
}

function buildChips(f: LeaderboardFilters): Chip[] {
  const chips: Chip[] = [];
  if (f.search?.trim()) {
    chips.push({ key: "search", label: `Search: "${f.search}"`, clear: { search: undefined } });
  }
  if (f.category) {
    chips.push({
      key: "category",
      label: `Category: ${f.category}`,
      clear: { category: undefined },
    });
  }
  if (f.archived) {
    chips.push({ key: "archived", label: "Include archived", clear: { archived: undefined } });
  }
  if (f.scoreMin != null || f.scoreMax != null) {
    const label = `Score: ${f.scoreMin ?? "—"}–${f.scoreMax ?? "—"}`;
    chips.push({ key: "score-range", label, clear: { scoreMin: undefined, scoreMax: undefined } });
  }
  if (f.scoreBand && f.scoreBand.length > 0) {
    chips.push({
      key: "scoreBand",
      label: `Band: ${f.scoreBand.join(", ")}`,
      clear: { scoreBand: undefined },
    });
  }
  if (f.family && f.family.length > 0) {
    chips.push({
      key: "family",
      label: `Family: ${f.family.join(", ")}`,
      clear: { family: undefined },
    });
  }
  if (f.raiseUnknown) {
    chips.push({ key: "raiseUnknown", label: "Stealth", clear: { raiseUnknown: undefined } });
  } else {
    if (f.raiseMin != null || f.raiseMax != null) {
      const label = `Raise: ${f.raiseMin != null ? fmtUsd(f.raiseMin) : "—"}–${
        f.raiseMax != null ? fmtUsd(f.raiseMax) : "—"
      }`;
      chips.push({
        key: "raise-range",
        label,
        clear: { raiseMin: undefined, raiseMax: undefined },
      });
    }
    if (f.raiseBucket && f.raiseBucket.length > 0) {
      chips.push({
        key: "raiseBucket",
        label: `Bucket: ${f.raiseBucket.join(", ")}`,
        clear: { raiseBucket: undefined },
      });
    }
  }
  if (f.trend) {
    chips.push({ key: "trend", label: `Trend: ${f.trend}`, clear: { trend: undefined } });
  }
  if (f.hasBackers) {
    chips.push({ key: "hasBackers", label: "Has backers", clear: { hasBackers: undefined } });
  }
  if (f.tgeConfidenceMin != null || f.tgeConfidenceMax != null) {
    const label = `TGE conf: ${f.tgeConfidenceMin ?? "—"}–${f.tgeConfidenceMax ?? "—"}`;
    chips.push({
      key: "tge-range",
      label,
      clear: { tgeConfidenceMin: undefined, tgeConfidenceMax: undefined },
    });
  }
  if (f.hasTgeHints) {
    chips.push({ key: "hasTgeHints", label: "TGE hints", clear: { hasTgeHints: undefined } });
  }
  if (f.hasToken != null) {
    chips.push({
      key: "hasToken",
      label: f.hasToken ? "Has token" : "No token",
      clear: { hasToken: undefined },
    });
  }
  if (f.tokenVerified && f.tokenVerified.length > 0) {
    chips.push({
      key: "tokenVerified",
      label: `Token on: ${f.tokenVerified.join(", ")}`,
      clear: { tokenVerified: undefined },
    });
  }
  if (f.ecosystem && f.ecosystem.length > 0) {
    chips.push({
      key: "ecosystem",
      label: `Ecosystem: ${f.ecosystem.join(", ")}`,
      clear: { ecosystem: undefined },
    });
  }
  if (f.auditedWithinDays != null) {
    chips.push({
      key: "auditedWithin",
      label: `Audited ≤${f.auditedWithinDays}d`,
      clear: { auditedWithinDays: undefined },
    });
  }
  if (f.auditedOlderThanDays != null) {
    chips.push({
      key: "auditedOlder",
      label: `Audit >${f.auditedOlderThanDays}d`,
      clear: { auditedOlderThanDays: undefined },
    });
  }
  if (f.discoverySource && f.discoverySource.length > 0) {
    chips.push({
      key: "discoverySource",
      label: `Source: ${f.discoverySource.join(", ")}`,
      clear: { discoverySource: undefined },
    });
  }
  if (f.subscoreKey && f.subscoreMin != null) {
    chips.push({
      key: "subscore",
      label: `${f.subscoreKey} ≥ ${f.subscoreMin}`,
      clear: { subscoreKey: undefined, subscoreMin: undefined },
    });
  }
  return chips;
}

export function LeaderboardActiveFilters({ filters, onPatch, onResetAll }: Props) {
  const chips = buildChips(filters);
  if (chips.length === 0) return null;

  return (
    <div className="leaderboard-active-filters">
      {chips.map((chip) => (
        <button
          key={chip.key}
          type="button"
          className="leaderboard-active-chip"
          onClick={() => onPatch(chip.clear)}
        >
          {chip.label}
          <span className="leaderboard-active-chip-x">×</span>
        </button>
      ))}
      <button type="button" className="leaderboard-reset-all" onClick={onResetAll}>
        Reset all
      </button>
    </div>
  );
}
